import { useState } from 'react';
import { Printer, Trash2 } from 'lucide-react';
import { LABEL_FORMATS, labelsHtml, type LabelFormat, tierText } from '../lib/labels';
import { printHtml } from '../lib/receipt';
import { post } from './api';
import { useMakilaLinked } from './makila';
import { Alert, Button, Card, Field, Input, Select } from './ui';
import { attempt, useLoad } from './useLoad';

interface Product { id: string; code: string; name: string; barcode: string | null; unit_price: string; sold_by_weight: boolean; price_tiers: { minQty: string; unitPrice: string }[]; active: boolean }

/** Etiquetas de preço para a prateleira, com o código de barras que a caixa lê. */
export function LabelsPage() {
  const { data, error, reload } = useLoad<{ items: Product[] }>('/products');
  const me = useLoad<{ tenant: { legal_name: string; trade_name: string | null } }>('/auth/me');
  const linked = useMakilaLinked();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<{ id: string; copies: string }[]>([]);
  const [format, setFormat] = useState<LabelFormat>('a4');
  const [err, setErr] = useState<string | null>(null);
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="text-muted">A carregar…</p>;
  const byId = new Map(data.items.map((p) => [p.id, p]));
  const s = q.trim().toLowerCase();
  const hits = s ? data.items.filter((p) => p.active && (p.barcode === q.trim() || p.code.toLowerCase() === s || p.name.toLowerCase().includes(s))).slice(0, 8) : [];
  const add = (id: string) => { setRows((r) => (r.some((x) => x.id === id) ? r : [...r, { id, copies: '1' }])); setQ(''); };
  const company = me.data ? me.data.tenant.trade_name ?? me.data.tenant.legal_name : '';
  const print = () => void printHtml(labelsHtml(rows.map((r) => {
    const p = byId.get(r.id)!;
    return { name: p.name, code: p.code, barcode: p.barcode, unitPrice: p.unit_price, soldByWeight: p.sold_by_weight, tierText: tierText(p.price_tiers), copies: Number(r.copies) || 0 };
  }), format, company));
  return (
    <div className="max-w-4xl space-y-4">
      {err && <Alert>{err}</Alert>}
      <Card title="Etiquetas de preço">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Artigo"><Input aria-label="Procurar artigo para etiqueta" placeholder="Nome, código ou código de barras" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const x = data.items.find((p) => p.barcode === q.trim() || p.code.toLowerCase() === s) ?? (hits.length === 1 ? hits[0] : undefined); if (x) add(x.id); } }} /></Field>
          <Field label="Formato"><Select value={format} onChange={(e) => setFormat(e.target.value as LabelFormat)}>{Object.entries(LABEL_FORMATS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}</Select></Field>
        </div>
        {hits.length > 0 && (
          <ul className="mt-1 divide-y divide-[var(--border)] rounded-lg border border-base">
            {hits.map((p) => <li key={p.id}><button className="flex h-11 w-full items-center gap-2 px-3 text-left hover:bg-surface-2" onClick={() => add(p.id)}><span className="font-mono text-xs text-muted">{p.code}</span> {p.name}</button></li>)}
          </ul>
        )}
        <ul className="mt-3 divide-y divide-[var(--border)]">
          {rows.map((r, i) => {
            const p = byId.get(r.id)!;
            return (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="min-w-0 flex-1">{p.name}<span className="block text-xs text-muted">{p.barcode ?? 'sem código de barras: sai o código do artigo'}</span></span>
                {!p.barcode && !linked && (
                  <Button variant="ghost" onClick={async () => { setErr(await attempt(() => post(`/products/${p.id}/internal-barcode`))); void reload(); }}>Gerar código</Button>
                )}
                <Input aria-label={`Etiquetas de ${p.name}`} inputMode="numeric" className="w-20" value={r.copies} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, copies: e.target.value.replace(/\D/g, '') } : x)))} />
                <button aria-label={`Retirar ${p.name}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="grid h-10 w-10 place-items-center rounded-lg text-brand-700"><Trash2 size={16} /></button>
              </li>
            );
          })}
        </ul>
        <Button className="mt-3" disabled={!rows.length} onClick={print}><Printer size={16} /> Imprimir {rows.reduce((n, r) => n + (Number(r.copies) || 0), 0)} etiqueta(s)</Button>
        <p className="mt-2 text-sm text-muted">"Gerar código" dá ao artigo um código interno (EAN-13 começado por 200), que fica no catálogo e chega às caixas.</p>
      </Card>
    </div>
  );
}
